/**
 * Client-side AES-GCM encryption for workspace data.
 *
 * Uses the user's email as the basis for key derivation via PBKDF2.
 * Only the user who encrypted the data can decrypt it — even if the
 * database is leaked, the workspace JSON is unreadable without the
 * correct email + salt.
 */

const SALT_LENGTH = 16;
const IV_LENGTH = 12;
const ITERATIONS = 100_000;

async function deriveKey(email: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(email.toLowerCase().trim()),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt a JSON-serializable object.
 * Returns a base64-encoded string: salt(16) + iv(12) + ciphertext
 */
export async function encryptWorkspace(data: unknown, email: string): Promise<string> {
  const encoder = new TextEncoder();
  const plaintext = encoder.encode(JSON.stringify(data));

  const salt = crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const key = await deriveKey(email, salt);

  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    plaintext
  );

  // Combine: salt + iv + ciphertext
  const combined = new Uint8Array(SALT_LENGTH + IV_LENGTH + ciphertext.byteLength);
  combined.set(salt, 0);
  combined.set(iv, SALT_LENGTH);
  combined.set(new Uint8Array(ciphertext), SALT_LENGTH + IV_LENGTH);

  return btoa(bytesToBinaryString(combined));
}

/**
 * Convert a Uint8Array to a binary string in 8 KB chunks. The naive
 * `String.fromCharCode(...combined)` spread blows the JS argument-count
 * limit (~65 535 args in V8) once the encrypted payload crosses ~50 KB,
 * producing "Maximum call stack size exceeded" on legitimately-sized
 * workspace saves (e.g. atlas-import of ~40 docs). Looping in 8 KB
 * chunks stays comfortably under the limit at every size.
 *
 * Same idiom used by every production base64-encoder. Keep CHUNK_SIZE
 * power-of-two and well below 65 535.
 */
function bytesToBinaryString(bytes: Uint8Array): string {
  const CHUNK_SIZE = 8192;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
    const slice = bytes.subarray(i, i + CHUNK_SIZE);
    binary += String.fromCharCode.apply(null, slice as unknown as number[]);
  }
  return binary;
}

/**
 * Decrypt a base64-encoded encrypted workspace string.
 * Returns the parsed JSON object.
 */
export async function decryptWorkspace<T = unknown>(encrypted: string, email: string): Promise<T> {
  const combined = Uint8Array.from(atob(encrypted), c => c.charCodeAt(0));

  const salt = combined.slice(0, SALT_LENGTH);
  const iv = combined.slice(SALT_LENGTH, SALT_LENGTH + IV_LENGTH);
  const ciphertext = combined.slice(SALT_LENGTH + IV_LENGTH);

  const key = await deriveKey(email, salt);

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  );

  const decoder = new TextDecoder();
  return JSON.parse(decoder.decode(plaintext));
}
